import React, { useState, useEffect } from "react";
import { SafeAreaView, StyleSheet, View } from "react-native";
import {
  DrawerContentScrollView,
  DrawerContentComponentProps
} from "@react-navigation/drawer";
import { useAuth } from "@/hooks/auth";
import { useCrisisResources } from "@/hooks/crisis";
import { CrisisCooldownError, useSession } from "@/hooks/session";
import { colors, layout } from "@/constants";
import { useRouter, usePathname, useGlobalSearchParams } from "expo-router";
import Logo from "./Logo";
import AppTitle from "./AppTitle";
import DrawerSection from "./DrawerSection";
import DrawerButton from "./DrawerButton";
import DrawerSessionsList from "./DrawerSessionsList";
import UserProfile from "./UserProfile";
import Interactive from "./base/Interactive";
import ItemContextMenu from "./base/ItemContextMenu";
import Text from "./base/Text";
import { logger } from "@/utils/logger";
import { showToast } from "@/utils/toast";
import { getSessions } from "@/store/sessions";
import { Session } from "@/store/types";
import * as apiClient from "@/utils/serenity-api-client";

/**
 * Custom drawer content component
 * Shows different content based on user authentication state
 */
export default function DrawerContent(props: DrawerContentComponentProps) {
  const { user, signOut, getIdToken } = useAuth();
  const { createSession } = useSession();
  const { openCooldown } = useCrisisResources();
  const router = useRouter();
  const pathname = usePathname();
  const params = useGlobalSearchParams();
  const [sessions, setSessions] = useState<Session[]>([]);

  // Load user sessions when user is available
  useEffect(() => {
    const loadSessions = async () => {
      if (!user?.uid) {
        setSessions([]);
        return;
      }

      try {
        const userSessions = await getSessions(user.uid);
        setSessions(userSessions);
      } catch (error) {
        logger.error("Failed to load sessions:", error);
        setSessions([]);
      }
    };

    loadSessions();
  }, [user?.uid]);

  const handleSessionPress = (sessionId: string) => {
    router.replace(`/chat?session=${sessionId}`);
    props.navigation.closeDrawer();
  };

  const handleSessionDelete = async (sessionId: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;

      const result = await apiClient.deleteSession(sessionId, token);
      if (result.success) {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        // If the deleted session was the current one, navigate away
        if (params.session === sessionId) {
          router.replace("/(drawer)/chat");
        }
      } else {
        logger.error("Failed to delete session:", result.message);
        showToast("Failed to delete session");
      }
    } catch (error) {
      logger.error("Failed to delete session:", error);
      showToast("Failed to delete session");
    }
  };

  // Special connect handler for new sessions
  const handleNewChat = async () => {
    try {
      const sessionId = await createSession(); // createSession sets auto-connect flag
      // Navigate to the new session URL (auto-connect will happen via useEffect)
      router.replace(`/chat?session=${sessionId}`);
    } catch (error) {
      if (error instanceof CrisisCooldownError) {
        logger.info("Session refused: crisis cooldown");
        openCooldown(error.body);
        return;
      }
      logger.error("Failed to create new session", error);
      showToast(
        error instanceof Error ? error.message : "Failed to create session"
      );
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      router.replace("/(drawer)/login");
    } catch (error) {
      logger.error("Logout failed:", error);
    }
  };

  const handleLogin = () => {
    router.replace("/(drawer)/login");
  };

  const handleSettingsPress = () => {
    router.replace("/(drawer)/settings");
  };

  const handleFactsPress = () => {
    router.replace("/(drawer)/facts");
  };

  const handlePatternsPress = () => {
    router.replace("/(drawer)/patterns");
  };

  const handleAccountPress = () => {
    router.replace("/(drawer)/account");
  };

  const handleHeaderPress = () => {
    router.replace("/(drawer)/chat");
  };

  return (
    <SafeAreaView style={styles.container}>
      <DrawerContentScrollView
        {...props}
        contentContainerStyle={styles.drawerContent}
      >
        {/* Header */}
        <DrawerSection showDivider>
          <Interactive
            style={styles.headerContainer}
            onPress={handleHeaderPress}
            accessibilityLabel="Go to chat"
          >
            <Logo width={48} height={48} style={styles.logo} />
            <AppTitle />
          </Interactive>
        </DrawerSection>

        {/* Sessions List */}
        {user && (
          <DrawerSection flex showDivider>
            {/* New Chat Button */}
            <DrawerButton
              text="New Session"
              icon="add_circle"
              onPress={handleNewChat}
            />
            {/* Facts Button */}
            <DrawerButton
              text="Facts"
              icon="fact_check"
              onPress={handleFactsPress}
              isSelected={
                pathname === "/(drawer)/facts" || pathname === "/facts"
              }
            />
            {/* Patterns Button */}
            <DrawerButton
              text="Patterns"
              icon="insights"
              onPress={handlePatternsPress}
              isSelected={
                pathname === "/(drawer)/patterns" || pathname === "/patterns"
              }
            />
            {user && (
              <DrawerButton
                text="Settings"
                icon="settings"
                onPress={handleSettingsPress}
                isSelected={
                  pathname === "/(drawer)/settings" || pathname === "/settings"
                }
              />
            )}
            {/* Chats Title */}
            <Text size="small" color="secondary" style={styles.sectionTitle}>
              Sessions
            </Text>
            <DrawerSessionsList
              sessions={sessions}
              currentSessionId={params.session as string | undefined}
              onSessionPress={handleSessionPress}
              onSessionDelete={handleSessionDelete}
            />
          </DrawerSection>
        )}

        {/* Bottom Section: User Info + Actions */}
        <DrawerSection>
          {user ? (
            <View style={styles.userLogoutRow}>
              <View style={styles.userProfileArea}>
                <UserProfile user={user} />
              </View>
              <ItemContextMenu
                items={[
                  {
                    icon: "account_circle",
                    label: "Account",
                    onPress: handleAccountPress
                  },
                  {
                    icon: "logout",
                    label: "Logout",
                    variant: "danger",
                    onPress: handleLogout
                  }
                ]}
              />
            </View>
          ) : (
            <DrawerButton
              text="Login"
              icon="login"
              variant="primary"
              onPress={handleLogin}
            />
          )}
        </DrawerSection>
      </DrawerContentScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surface
  },
  drawerContent: {
    flex: 1
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: layout.space.md,
    borderRadius: layout.borderRadius.medium
  },
  logo: {
    marginRight: layout.space.sm
  },
  sectionTitle: {
    // Match the horizontal padding of DrawerButton/DrawerSession rows so the
    // "Sessions" label aligns with the item text rather than sitting flush left.
    paddingHorizontal: layout.space.md,
    paddingVertical: layout.space.md
  },
  userLogoutRow: {
    flexDirection: "row",
    alignItems: "center"
  },
  userProfileArea: {
    flex: 1
  }
});
